import React from 'react';
import { PresentationRole, PRESENTATION_ROLES } from '../../types/role';
import { Language } from '../../types/kundali';
import { Sparkles, Compass, Award, Shield } from 'lucide-react';

interface RoleSwitcherProps {
  activeRole: PresentationRole;
  onRoleChange: (role: PresentationRole) => void;
  lang: Language;
  className?: string;
  allowedRoles?: PresentationRole[];
}

const ROLE_ICONS: Record<PresentationRole, React.ComponentType<{ className?: string }>> = {
  BASIC: Sparkles,
  PRO: Compass,
  ACHARYA: Award,
  MANAGEMENT: Shield
};

export const RoleSwitcher: React.FC<RoleSwitcherProps> = ({
  activeRole,
  onRoleChange,
  lang,
  className = '',
  allowedRoles = ['BASIC', 'PRO', 'ACHARYA', 'MANAGEMENT']
}) => {
  const isHi = lang === 'hi';

  return (
    <div
      id="role-presentation-switcher"
      role="tablist"
      aria-label={isHi ? 'प्रस्तुति दृष्टिकोण चयनकर्ता' : 'Presentation Role Selector'}
      className={`bg-astro-card p-1 rounded-2xl border border-astro-border shadow-xs flex items-center gap-1 overflow-x-auto no-scrollbar whitespace-nowrap ${className}`}
    >
      {allowedRoles.map((roleKey) => {
        const role = PRESENTATION_ROLES[roleKey];
        if (!role) return null;
        const Icon = ROLE_ICONS[roleKey] || Sparkles;
        const isSelected = activeRole === roleKey;

        return (
          <button
            key={roleKey}
            role="tab"
            aria-selected={isSelected}
            aria-controls={`role-view-${roleKey.toLowerCase()}`}
            id={`role-tab-${roleKey.toLowerCase()}`}
            onClick={() => onRoleChange(roleKey)}
            className={`shrink-0 flex-1 min-h-[42px] px-3 py-1.5 rounded-xl flex items-center justify-center gap-2 transition-all duration-200 text-xs font-bold touch-manipulation focus:outline-none focus:ring-2 focus:ring-astro-primary focus:ring-offset-1 select-none whitespace-nowrap ${
              isSelected
                ? 'bg-astro-primary text-white shadow-xs'
                : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-border/50'
            }`}
          >
            <Icon
              className={`w-4 h-4 shrink-0 transition-transform ${
                isSelected ? 'text-astro-accent scale-105' : 'text-astro-secondary-text'
              }`}
            />
            <div className="flex flex-col items-start text-left">
              <span className="leading-tight font-serif tracking-wide">{isHi ? role.labelHi : role.labelEn}</span>
              <span className={`hidden md:inline text-[9px] font-normal leading-none mt-0.5 ${
                isSelected ? 'text-white/80' : 'text-astro-secondary-text/80'
              }`}>
                {isHi ? role.subtitleHi : role.subtitleEn}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
};
